import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { businessInfo } from '../../mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FormField = ({ id, label, required, children }) => (
  <div className="mb-6">
    <Label htmlFor={id} className="text-gray-900 font-semibold mb-2 block">
      {label} {required && '*'}
    </Label>
    {children}
  </div>
);

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast.success("Inspection Request Received!", {
          description: response.data.message,
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Please try calling us directly at " + businessInfo.phone,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <Label htmlFor="name" className="text-gray-900 font-semibold mb-2 block">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Smith"
            required
            className="border-gray-200"
            data-testid="contact-name-input"
          />
        </div>

        <div>
          <Label htmlFor="phone" className="text-gray-900 font-semibold mb-2 block">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="0400 000 000"
            required
            className="border-gray-200"
            data-testid="contact-phone-input"
          />
        </div>
      </div>

      <FormField id="email" label="Email Address" required>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="john@example.com"
          required
          className="border-gray-200"
          data-testid="contact-email-input"
        />
      </FormField>

      <FormField id="address" label="Property Address" required>
        <Input
          id="address"
          name="address"
          type="text"
          value={formData.address}
          onChange={handleChange}
          placeholder="123 Main Street, Sydney NSW"
          required
          className="border-gray-200"
          data-testid="contact-address-input"
        />
      </FormField>

      <FormField id="message" label="Additional Details">
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your roofing needs..."
          rows={4}
          className="border-gray-200"
          data-testid="contact-message-input"
        />
      </FormField>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary hover:bg-accent text-white text-lg py-6"
        data-testid="contact-submit-btn"
      >
        {isSubmitting ? (
          "Sending..."
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Book a Free Inspection
          </>
        )}
      </Button>
    </form>
  );
};
